import React, { useState } from "react"
import { ContactInfo } from "../Utils/Data"
import PageTitle from "./include/PageTitle"
import { useI18n } from "../i18n/I18nContext"
import Seo from "../Components/Seo"
import { fetchApiJson } from "../config/apiBase"

const officeMapSrc =
    ContactInfo.office_map_embed_url ||
    (ContactInfo.office_map_query
        ? `https://maps.google.com/maps?hl=en&q=${encodeURIComponent(ContactInfo.office_map_query)}&z=${ContactInfo.office_map_zoom || 17}&iwloc=B&output=embed`
        : `https://maps.google.com/maps?hl=en&q=${ContactInfo.office_map_lat},${ContactInfo.office_map_lng}&z=${ContactInfo.office_map_zoom}&iwloc=B&output=embed`)
const officeMapLink =
    ContactInfo.office_maps_share_url ||
    `https://www.google.com/maps?q=${ContactInfo.office_map_lat},${ContactInfo.office_map_lng}`
const contactPhones = [ContactInfo.contact_phone, ContactInfo.contact_phone2].filter(Boolean)
const contactEmails = [ContactInfo.contact_email].filter(Boolean)

const QUOTE_SERVICE_PLACEHOLDER = "Select service type"
const QUOTE_SERVICE_TYPES = [
    "Property Management",
    "Real Estate Development",
    "Real Estate Brokerage",
    "Real Estate Marketing",
    "Maintenance & Operations",
    "Other / general inquiry",
]

const initialForm = {
    name: "",
    email: "",
    serviceType: "",
    phone: "",
    message: "",
}

const Contact = () => {
    const { t } = useI18n()
    const [form, setForm] = useState(initialForm)
    const [submitting, setSubmitting] = useState(false)
    const [feedback, setFeedback] = useState({ type: "", message: "" })

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target
        setForm((current) => ({
            ...current,
            [name]: type === "checkbox" ? checked : value,
        }))
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        setSubmitting(true)
        setFeedback({ type: "", message: "" })

        try {
            const { json } = await fetchApiJson("/api/services/inquiries", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: form.name,
                    email: form.email,
                    phone: form.phone,
                    message: form.message,
                    sourceTitle: form.serviceType || "Contact Us Inquiry",
                    sourceSlug: "contact-us",
                }),
            })

            if (!json?.success) {
                throw new Error(json?.message || "We could not send your inquiry right now. Please try again.")
            }

            setForm(initialForm)
            setFeedback({
                type: "success",
                message: "Your inquiry has been sent successfully.",
            })
        } catch (error) {
            setFeedback({
                type: "error",
                message: error?.message || "We could not send your inquiry right now. Please try again.",
            })
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div>

            <Seo
                title="Contact QTECH | Real Estate Services in Al Khobar, Saudi Arabia"
                titleAr="تواصل مع شركة تقنيات الجودة | خدمات عقارية في الخبر"
                description="Contact QTECH in Al Khobar, Saudi Arabia for property management, brokerage, real estate marketing, maintenance and operations, and property development inquiries."
                descriptionAr="تواصل مع شركة تقنيات الجودة في الخبر للاستفسار عن الخدمات العقارية، إدارة الأملاك، الوساطة العقارية، التسويق العقاري، صيانة المباني، والتطوير العقاري."
                keywords="contact QTECH, real estate company Al Khobar, real estate services Saudi Arabia, property management Al Khobar, real estate brokerage Saudi Arabia"
                keywordsAr="تواصل مع شركة تقنيات الجودة، شركة عقارية في الخبر، خدمات عقارية في الخبر، إدارة أملاك في الخبر، وسيط عقاري في الخبر، تسويق عقاري في الخبر، صيانة مباني في الخبر، تطوير عقاري في الخبر"
                path="/contact"
                image="/img/bg/14.jpg"
            />
            
            <PageTitle titleKey="pages.contact.title" currentPageKey="pages.contact.title" bgImage="/img/bg/14.jpg" endContent={null} />

            <div className="ltn__contact-address-area mb-90">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-4">
                            <div className="ltn__contact-address-item ltn__contact-address-item-3 box-shadow">
                                <div className="ltn__contact-address-icon">
                                    <img src="img/icons/10.png" alt="Icon Image" />
                                </div>
                                <h3 className="animated fadeIn">{t("contactPage.emailLabel")}</h3>
                                <p>
                                    {contactEmails.map((email, index) => (
                                        <React.Fragment key={email}>
                                            <a href={`mailto:${email}`}>{email}</a>
                                            {index < contactEmails.length - 1 ? <br /> : null}
                                        </React.Fragment>
                                    ))}
                                </p>
                            </div>
                        </div>
                        <div className="col-lg-4">
                            <div className="ltn__contact-address-item ltn__contact-address-item-3 box-shadow">
                                <div className="ltn__contact-address-icon">
                                    <img src="img/icons/11.png" alt="Icon Image" />
                                </div>
                                <h3 className="animated fadeIn">{t("contactPage.phoneLabel")}</h3>
                                <p>
                                    {contactPhones.map((phone, index) => (
                                        <React.Fragment key={phone}>
                                            <a href={`tel:${phone}`}>{phone}</a>
                                            {index < contactPhones.length - 1 ? <br /> : null}
                                        </React.Fragment>
                                    ))}
                                </p>
                            </div>
                        </div>
                        <div className="col-lg-4">
                            <div className="ltn__contact-address-item ltn__contact-address-item-3 box-shadow">
                                <div className="ltn__contact-address-icon">
                                    <img src="img/icons/12.png" alt="Icon Image" />
                                </div>
                                <h3 className="animated fadeIn">{t("contactPage.officeLabel")}</h3>
                                <p>
                                    <a href={officeMapLink} target="_blank" rel="noreferrer">
                                        {ContactInfo.office_address_line1}
                                    </a>
                                    <br />
                                    {ContactInfo.office_address_line2}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>



            <div className="ltn__contact-message-area mb-120 mb--100">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="ltn__form-box contact-form-box box-shadow white-bg">
                                <h4 className="title-2">{t("contactPage.quoteTitle")}</h4>
                                <form
                                    id="contact-form"
                                    onSubmit={handleSubmit}
                                >
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="input-item input-item-name ltn__custom-icon">
                                                <input
                                                    type="text"
                                                    name="name"
                                                    placeholder="Enter your name"
                                                    value={form.name}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="input-item input-item-email ltn__custom-icon">
                                                <input
                                                    type="email"
                                                    name="email"
                                                    placeholder="Enter email address"
                                                    value={form.email}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="input-item">
                                                <select
                                                    name="serviceType"
                                                    value={form.serviceType}
                                                    onChange={handleChange}
                                                    required
                                                >
                                                    <option value="">{QUOTE_SERVICE_PLACEHOLDER}</option>
                                                    {QUOTE_SERVICE_TYPES.map((label) => (
                                                        <option key={label} value={label}>
                                                            {label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="input-item input-item-phone ltn__custom-icon">
                                                <input
                                                    type="text"
                                                    name="phone"
                                                    placeholder="Enter phone number"
                                                    value={form.phone}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="input-item input-item-textarea ltn__custom-icon">
                                        <textarea
                                            name="message"
                                            placeholder="Enter message"
                                            value={form.message}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="btn-wrapper mt-0">
                                        <button
                                            className="btn theme-btn-1 btn-effect-1 text-uppercase"
                                            type="submit"
                                            disabled={submitting}
                                        >
                                            {submitting ? "Sending..." : t("contactPage.submit")}
                                        </button>
                                    </div>
                                    {feedback.message ? (
                                        <p className={`form-messege mb-0 mt-20 ${feedback.type === "error" ? "text-danger" : "text-success"}`}>
                                            {feedback.message}
                                        </p>
                                    ) : null}
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>



            <div className="google-map mb-120" style={{ position: "relative" }}>
                <iframe
                    title="QTECH office map"
                    src={officeMapSrc}
                    width="100%"
                    height="100%"
                    style={{ border: 0, display: "block", minHeight: "480px" }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                />
                <div
                    aria-hidden="true"
                    style={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        transform: "translate(-50%, -100%)",
                        pointerEvents: "none",
                        zIndex: 3,
                    }}
                >
                    <span
                        style={{
                            width: "28px",
                            height: "28px",
                            display: "block",
                            borderRadius: "50% 50% 50% 0",
                            background: "#bba55c",
                            transform: "rotate(-45deg)",
                            border: "2px solid #ffffff",
                            boxShadow: "0 8px 18px rgba(0, 0, 0, 0.28)",
                            position: "relative",
                        }}
                    >
                        <span
                            style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                width: "8px",
                                height: "8px",
                                background: "#ffffff",
                                borderRadius: "50%",
                                transform: "translate(-50%, -50%) rotate(45deg)",
                            }}
                        />
                    </span>
                </div>
            </div>



        </div>
    )
}

export default Contact
